import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { CARD_GRID_COLUMNS_CLASSES } from '@/constants/layout';
import { cn } from '@/lib/utils';

interface GridLoadingStateProps {
    isLoading: boolean;
    isEmpty: boolean;
    isLoadingMore?: boolean;
    isReachingEnd?: boolean;
    emptyMessage?: string;
    loadingMoreMessage?: string;
    endMessage?: string;
    hideEndMessage?: boolean;
    skeletonComponent?: ReactNode;
    skeletonCount?: number;
    gridClassName?: string;
    children?: ReactNode;
}

export function GridLoadingState({
    isLoading,
    isEmpty,
    isLoadingMore = false,
    isReachingEnd = false,
    emptyMessage,
    loadingMoreMessage,
    endMessage,
    hideEndMessage = false,
    skeletonComponent,
    skeletonCount = 6,
    gridClassName = cn('grid gap-4 p-4', CARD_GRID_COLUMNS_CLASSES),
    children,
}: GridLoadingStateProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const defaultEmptyMessage =
        emptyMessage || t('gridLoadingState.noItemsFound');
    const defaultLoadingMoreMessage =
        loadingMoreMessage || t('gridLoadingState.loadingMore');
    const defaultEndMessage =
        endMessage || t('gridLoadingState.allItemsLoaded');
    if (isLoading) {
        return (
            <div className={gridClassName}>
                {Array.from({ length: skeletonCount }).map((_, index) => (
                    <div key={index}>{skeletonComponent}</div>
                ))}
            </div>
        );
    }

    if (isEmpty && !isLoading && !isLoadingMore) {
        return (
            <div className="flex justify-center items-center h-full">
                <p className="text-grey-300">{defaultEmptyMessage}</p>
            </div>
        );
    }

    return (
        <>
            <div className={gridClassName}>{children}</div>
            {isLoadingMore && (
                <div className="flex justify-center py-4">
                    <p className="text-grey-300 text-sm">
                        {defaultLoadingMoreMessage}
                    </p>
                </div>
            )}
            {isReachingEnd && !isEmpty && !hideEndMessage ? (
                <div className="flex justify-center py-4">
                    <p className="text-grey-400 text-sm">{defaultEndMessage}</p>
                </div>
            ) : null}
        </>
    );
}
